import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Checkbox,
  FormHelperText,
  InputAdornment,
  Autocomplete,
  Chip,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar";
import OrganizationTypesField from "../../components/company/OrganizationTypesField";
import { useNavigate } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import { useCreateCompanyMutation } from "../../store/apis/companyApi";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { components, operations } from "../../types/api";
import { LEGAL_NOTE_OPTIONS } from "../../utils/billing/legalNotes";
import { HoldedContactPicker } from "../../components/billing";
import { HoldedContactPreview } from "../../store/apis/companyApi";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

type CreateCompanyRequest =
  operations["CreateCompany"]["requestBody"]["content"]["application/json"];

type CreateCompanyBillingRequest =
  components["schemas"]["Omit_CompanyBilling.id-or-createdAt-or-updatedAt-or-companyId_"];

// Campos que la empresa no puede guardar vacíos. Los mismos que exige la
// pantalla de edición: si aquí fuera más laxo, se crearían empresas que luego
// no se dejan editar.
const REQUIRED_COMPANY_FIELDS = [
  "nameCommercial",
  "nameFiscal",
  "nif",
] as const;

type RequiredCompanyField = (typeof REQUIRED_COMPANY_FIELDS)[number];

// Un billing sin estos datos no sirve para facturar: la razón social y el NIF
// son los que van a la factura de Holded, y el contacto es a quien se le manda.
const REQUIRED_BILLING_FIELDS = [
  "nameFiscal",
  "nif",
  "contactName",
  "email",
  "phoneNumber",
] as const;

type RequiredBillingField = (typeof REQUIRED_BILLING_FIELDS)[number];

const NewCompany: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [createCompany, { isLoading, data: createdData, error: createError }] =
    useCreateCompanyMutation();
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState<
    Omit<CreateCompanyRequest, "feeVariable" | "feeFixed"> & {
      feeVariable: string | number;
      feeFixed: string | number;
    }
  >({
    nameCommercial: "",
    organizationTypes: [],
    nameFiscal: "",
    nif: "",
    feeVariable: "0.00",
    feeFixed: "0.00",
    paymentTerms: "",
    phone: "",
    email: "",
    logoUrl: "",
    imageBase64: "",
    dv360AdvertiserIds: [],
    outbrainAccountIds: [],
    taboolaAccountIds: [],
    spotifyAdAccountIds: [],
    hivestackAdvertiserIds: [],
    amazonAdvertiserIds: [],
    metaAdAccountIds: [],
    tiktokAdvertiserIds: [],
    googleAdsCustomerIds: [],
    redditAdAccountIds: [],
  });

  // Track validation errors for integration fields
  const [integrationErrors, setIntegrationErrors] = useState<{
    dv360AdvertiserIds?: string;
    outbrainAccountIds?: string;
    taboolaAccountIds?: string;
    spotifyAdAccountIds?: string[];
    hivestackAdvertiserIds?: string[];
    amazonAdvertiserIds?: string[];
    metaAdAccountIds?: string[];
    tiktokAdvertiserIds?: string[];
    googleAdsCustomerIds?: string[];
    redditAdAccountIds?: string[];
  }>({});

  const [billingEntries, setBillingEntries] = useState<
    CreateCompanyBillingRequest[]
  >([
    {
      nameFiscal: "",
      nif: "",
      contactName: "",
      email: "",
      phoneNumber: "",
      address: "",
      city: "",
      postalCode: "",
      province: "",
      country: "",
      holdedContactId: "",
      legalNote: "",
      isDefault: true,
    },
  ]);

  // Obligatorios vacíos en el último intento de guardar, resaltados en pantalla.
  const [missingCompanyFields, setMissingCompanyFields] = useState<
    RequiredCompanyField[]
  >([]);
  const [missingBillingFields, setMissingBillingFields] = useState<
    Record<number, RequiredBillingField[]>
  >({});

  const [previewImage, setPreviewImage] = useState<string>();

  const handleInputChange =
    (field: keyof CreateCompanyRequest) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      setFormData({
        ...formData,
        [field]: newValue,
      });

      if (newValue.trim()) {
        setMissingCompanyFields((previous) =>
          previous.filter((missingField) => missingField !== field)
        );
      }
    };

  // This function is no longer used as we're using inline handlers for better type safety

  const isBillingFieldMissing = (index: number, field: RequiredBillingField) =>
    (missingBillingFields[index] ?? []).includes(field);

  // Props comunes de un campo obligatorio de billing: ancla para el scroll,
  // estado de error y el texto de ayuda, sin repetirlos campo a campo.
  const billingFieldProps = (index: number, field: RequiredBillingField) => ({
    id: `billing-field-${index}-${field}`,
    error: isBillingFieldMissing(index, field),
    helperText: isBillingFieldMissing(index, field)
      ? t("company.errors.requiredField")
      : "",
    inputProps: { "aria-required": true },
  });

  const updateBillingEntry = (
    index: number,
    patch: Partial<CreateCompanyBillingRequest>
  ) => {
    setBillingEntries((previous) =>
      previous.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
    );

    const filled = Object.entries(patch)
      .filter(([, value]) => String(value ?? "").trim())
      .map(([field]) => field);
    if (filled.length === 0) return;

    setMissingBillingFields((previous) => {
      const missing = previous[index];
      if (!missing) return previous;
      const remaining = missing.filter((field) => !filled.includes(field));
      const next = { ...previous };
      if (remaining.length > 0) next[index] = remaining;
      else delete next[index];
      return next;
    });
  };

  // Vuelca en el billing los datos fiscales que devuelve Holded. Holded es la
  // fuente de verdad de la facturación, así que sus campos se sobrescriben tal
  // cual; lo que no viene de allí (nota legal, billing por defecto) se respeta.
  const fillBillingFromHolded = (
    index: number,
    preview: HoldedContactPreview
  ) => {
    updateBillingEntry(index, {
      holdedContactId: preview.holdedContactId,
      nameFiscal: preview.nameFiscal,
      nif: preview.nif,
      email: preview.email,
      phoneNumber: preview.phoneNumber,
      address: preview.address,
      city: preview.city,
      postalCode: preview.postalCode,
      province: preview.province,
      country: preview.country,
      contactName: preview.contactName,
    });
  };

  const addBillingEntry = () => {
    setBillingEntries([
      ...billingEntries,
      {
        nameFiscal: "",
        nif: "",
        contactName: "",
        email: "",
        phoneNumber: "",
        address: "",
        city: "",
        postalCode: "",
        province: "",
        country: "",
        holdedContactId: "",
        legalNote: "",
        isDefault: false,
      },
    ]);
  };

  const removeBillingEntry = (index: number) => {
    if (billingEntries.length > 1) {
      const newBillingEntries = [...billingEntries];
      newBillingEntries.splice(index, 1);

      // Ensure at least one billing entry is marked as default
      if (billingEntries[index].isDefault && newBillingEntries.length > 0) {
        newBillingEntries[0].isDefault = true;
      }

      setBillingEntries(newBillingEntries);
    }
  };

  const handleDefaultBillingChange = (index: number) => {
    const newBillingEntries = [...billingEntries];

    // Set all entries to non-default
    newBillingEntries.forEach((entry, i) => {
      newBillingEntries[i] = { ...entry, isDefault: false };
    });

    // Set the selected entry as default
    newBillingEntries[index] = { ...newBillingEntries[index], isDefault: true };

    setBillingEntries(newBillingEntries);
  };

  useEffect(() => {
    if (!createdData?.data) return;

    // La empresa y sus billings se crean en una sola llamada, así que al llegar
    // aquí ya está todo guardado: no queda un segundo paso que pueda fallar y
    // dejar la empresa a medias.
    dispatch(
      setToastMessage({
        message: t("company.success.createSuccess"),
        severity: "success",
      })
    );
    navigate("/companies");
  }, [createdData, dispatch, navigate, t]);

  useEffect(() => {
    console.log("CREATE COMPANY ERROR:", createError);
    if (createError) {
      let errorMessage = t("common.unknownError");
      if (
        "data" in createError &&
        createError.data &&
        typeof createError.data === "object"
      ) {
        const errorData = createError.data as { message?: string };
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if ("error" in createError) {
        errorMessage = createError.error;
      }
      dispatch(
        setToastMessage({
          message: t("company.errors.createError", { error: errorMessage }),
          severity: "error",
        })
      );
    }
  }, [createError, dispatch, t]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (billingEntries.length === 0) {
      dispatch(
        setToastMessage({
          message: t("company.errors.missingBilling", {
            defaultValue:
              "Please add at least one billing contact before submitting",
          }),
          severity: "error",
        })
      );
      return;
    }

    const missingCompany = REQUIRED_COMPANY_FIELDS.filter(
      (field) => !String(formData[field] ?? "").trim()
    );

    const missingBilling: Record<number, RequiredBillingField[]> = {};
    billingEntries.forEach((billing, index) => {
      const missing = REQUIRED_BILLING_FIELDS.filter(
        (field) => !String(billing[field] ?? "").trim()
      );
      if (missing.length > 0) missingBilling[index] = missing;
    });

    setMissingCompanyFields(missingCompany);
    setMissingBillingFields(missingBilling);

    if (missingCompany.length > 0 || Object.keys(missingBilling).length > 0) {
      const labels = [
        ...missingCompany.map((field) => t(`company.${field}`)),
        ...Object.entries(missingBilling).flatMap(([index, fields]) =>
          fields.map(
            (field) =>
              `${t("company.billing")} ${Number(index) + 1}: ${t(
                `billing.${field}`
              )}`
          )
        ),
      ];

      dispatch(
        setToastMessage({
          message: t("company.errors.missingFieldsDetail", {
            fields: labels.join(", "),
            defaultValue:
              "Please fill in all required fields before submitting",
          }),
          severity: "error",
        })
      );

      const firstFieldId =
        missingCompany.length > 0
          ? `company-field-${missingCompany[0]}`
          : `billing-field-${Object.keys(missingBilling)[0]}-${
              Object.values(missingBilling)[0][0]
            }`;
      document
        .getElementById(firstFieldId)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });

      return;
    }

    // Check for duplicate integration IDs before submission
    const {
      dv360AdvertiserIds,
      outbrainAccountIds,
      taboolaAccountIds,
      spotifyAdAccountIds,
      hivestackAdvertiserIds,
      amazonAdvertiserIds,
      metaAdAccountIds,
      tiktokAdvertiserIds,
      googleAdsCustomerIds,
      redditAdAccountIds,
    } = formData;
    const integrationIds = [
      ...(dv360AdvertiserIds || []),
      ...(outbrainAccountIds || []),
      ...(taboolaAccountIds || []),
      ...(spotifyAdAccountIds || []),
      ...(hivestackAdvertiserIds || []),
      ...(amazonAdvertiserIds || []),
      ...(metaAdAccountIds || []),
      ...(tiktokAdvertiserIds || []),
      ...(googleAdsCustomerIds || []),
      ...(redditAdAccountIds || []),
    ].filter((id) => id.trim() !== "");
    const uniqueIds = new Set(integrationIds);

    if (integrationIds.length !== uniqueIds.size) {
      // Duplicate IDs found, validate all fields to show errors
      const newErrors: { [key: string]: string } = {};
      const idCounts: { [key: string]: string[] } = {};

      const registerId = (value: string, field: string) => {
        if (!value.trim()) return;
        idCounts[value] = [...(idCounts[value] || []), field];
      };

      (dv360AdvertiserIds || []).forEach((id) =>
        registerId(id, "dv360AdvertiserIds")
      );
      (outbrainAccountIds || []).forEach((id) =>
        registerId(id, "outbrainAccountIds")
      );
      (taboolaAccountIds || []).forEach((id) =>
        registerId(id, "taboolaAccountIds")
      );
      (spotifyAdAccountIds || []).forEach((id) =>
        registerId(id, "spotifyAdAccountIds")
      );
      (hivestackAdvertiserIds || []).forEach((id) =>
        registerId(id, "hivestackAdvertiserIds")
      );
      (amazonAdvertiserIds || []).forEach((id) =>
        registerId(id, "amazonAdvertiserIds")
      );
      (metaAdAccountIds || []).forEach((id) =>
        registerId(id, "metaAdAccountIds")
      );
      (tiktokAdvertiserIds || []).forEach((id) =>
        registerId(id, "tiktokAdvertiserIds")
      );
      (googleAdsCustomerIds || []).forEach((id) =>
        registerId(id, "googleAdsCustomerIds")
      );
      (redditAdAccountIds || []).forEach((id) =>
        registerId(id, "redditAdAccountIds")
      );

      Object.values(idCounts).forEach((fields) => {
        if (fields.length > 1) {
          fields.forEach((field) => {
            newErrors[field] = t("company.errors.duplicateIntegrationId", {
              defaultValue: "This ID is already used for another integration",
            });
          });
        }
      });

      setIntegrationErrors((prev) => ({
        ...prev,
        ...newErrors,
      }));

      dispatch(
        setToastMessage({
          message: t("company.errors.duplicateIntegrationIds", {
            defaultValue:
              "Please fix duplicate integration IDs before submitting",
          }),
          severity: "error",
        })
      );

      return;
    }

    // El billing por defecto es el contacto de facturación de la empresa, así que
    // el email y el teléfono de la Company salen de ahí en vez de pedirse aparte.
    const defaultBilling =
      billingEntries.find((billing) => billing.isDefault) || billingEntries[0];

    // No duplicates, proceed with form submission
    const payload = {
      ...formData,
      email: defaultBilling.email || "",
      phone: defaultBilling.phoneNumber || "",
      billings: billingEntries,
      feeVariable:
        formData.feeVariable !== "" && formData.feeVariable != null
          ? parseFloat(String(formData.feeVariable))
          : 0,
      feeFixed:
        formData.feeFixed !== "" && formData.feeFixed != null
          ? parseFloat(String(formData.feeFixed))
          : 0,
    };

    console.log("SUBMITTING NEW COMPANY FORM DATA:", payload);
    createCompany(payload);
  };

  const handleGenerateDummyData = () => {
    const randomSuffix = Math.floor(Math.random() * 10000);
    setFormData({
      nameCommercial: `Test Company ${randomSuffix}`,
      nameFiscal: `Test Fiscal S.L. ${randomSuffix}`,
      nif: `B${Math.floor(Math.random() * 89999999 + 10000000)}`,
      feeVariable: 10,
      feeFixed: 1000,
      phone: `+34 600 00 ${String(randomSuffix).padStart(4, "0")}`,
      email: `test${randomSuffix}@example.com`,
      logoUrl: "",
      imageBase64: "",
      dv360AdvertiserIds: [`DV${randomSuffix}`],
      outbrainAccountIds: [`OB${randomSuffix}`],
      taboolaAccountIds: [`TB${randomSuffix}`],
      spotifyAdAccountIds: [`SP${randomSuffix}`],
      hivestackAdvertiserIds: [`HS${randomSuffix}`],
      amazonAdvertiserIds: [`AMZ${randomSuffix}`],
      metaAdAccountIds: [`META${randomSuffix}`],
      tiktokAdvertiserIds: [`TT${randomSuffix}`],
      googleAdsCustomerIds: [`GADS${randomSuffix}`],
      redditAdAccountIds: [`a2_${randomSuffix}`],
    });
    setBillingEntries([
      {
        nameFiscal: "Demo Company SL",
        nif: "B12345678",
        contactName: "John Doe",
        email: "billing@acmecorp.com",
        phoneNumber: "+1 (555) 123-4567",
        address: "123 Business Ave, Suite 100",
        city: "San Francisco",
        postalCode: "94105",
        province: "California",
        country: "USA",
        holdedContactId: "",
        legalNote: "",
        isDefault: true,
      },
    ]);
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files?.[0]) {
      setUploading(true);
      try {
        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          setPreviewImage(base64String);
          setFormData((prev) => ({
            ...prev,
            imageBase64: base64String.split(",")[1],
            logoUrl: "",
          }));
          setUploading(false);
        };
        reader.readAsDataURL(file);
      } catch {
        dispatch(
          setToastMessage({
            message: t("company.errors.imageUploadError"),
            severity: "error",
          })
        );
        setUploading(false);
      }
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h3" gutterBottom>
              {t("company.createCompany")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {process.env.REACT_APP_ENV === "local" && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleGenerateDummyData}
                sx={{
                  height: "fit-content",
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                }}
              >
                {t("common.generateDummy", "Dummy Data")}
              </Button>
            )}
            <Button
              startIcon={<KeyboardBackspaceIcon />}
              onClick={() => navigate("/companies")}
              sx={{
                height: "fit-content",
                borderRadius: 50,
                textTransform: "none",
                fontFamily: "Nunito",
                fontWeight: 700,
                px: 3,
                py: 1.5,
                borderColor: theme.palette.grey[200],
                color: theme.palette.grey[900],
                "&:hover": {
                  borderColor: theme.palette.grey[300],
                  backgroundColor: theme.palette.grey[50],
                },
              }}
            >
              {t("common.back")}
            </Button>
          </Box>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* Logo Upload Section */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: "8px",
              p: 1.5,
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Nunito",
                fontWeight: 700,
                color: theme.palette.grey[900],
                pb: 1,
              }}
              variant="h6"
              gutterBottom
            >
              {t("company.logo")}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              {previewImage && (
                <Box
                  component="img"
                  src={previewImage}
                  alt={t("company.logo")}
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 9999,
                    border: `2px solid ${theme.palette.grey[200]}`,
                  }}
                />
              )}
              <Button
                component="label"
                variant="outlined"
                startIcon={<PhotoCamera />}
                disabled={uploading}
                sx={{
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[900],
                  "&:hover": {
                    borderColor: theme.palette.grey[300],
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                {uploading ? (
                  <CircularProgress size={24} />
                ) : (
                  t("company.uploadLogo")
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>
          </Box>

          {/* Company Details Section */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: "8px",
              p: 1.5,
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Nunito",
                fontWeight: 700,
                color: theme.palette.grey[900],
                pb: 1,
              }}
              variant="h6"
              gutterBottom
            >
              {t("company.details")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 2,
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.nameCommercial")}
                  <Box
                    component="span"
                    sx={{ color: theme.palette.error.main, ml: 0.5 }}
                  >
                    *
                  </Box>
                </Typography>
                <TextField
                  id="company-field-nameCommercial"
                  fullWidth
                  value={formData.nameCommercial}
                  onChange={handleInputChange("nameCommercial")}
                  error={missingCompanyFields.includes("nameCommercial")}
                  helperText={
                    missingCompanyFields.includes("nameCommercial")
                      ? t("company.errors.requiredField")
                      : ""
                  }
                  inputProps={{ "aria-required": true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: missingCompanyFields.includes(
                          "nameCommercial"
                        )
                          ? theme.palette.error.main
                          : theme.palette.grey[200],
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      fontFamily: "Nunito",
                      ml: 2,
                    },
                  }}
                />
              </Box>
              <OrganizationTypesField
                value={formData.organizationTypes ?? []}
                onChange={(next) =>
                  setFormData((prev) => ({
                    ...prev,
                    organizationTypes: next,
                  }))
                }
              />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.nameFiscal")}
                  <Box
                    component="span"
                    sx={{ color: theme.palette.error.main, ml: 0.5 }}
                  >
                    *
                  </Box>
                </Typography>
                <TextField
                  id="company-field-nameFiscal"
                  fullWidth
                  value={formData.nameFiscal}
                  onChange={handleInputChange("nameFiscal")}
                  error={missingCompanyFields.includes("nameFiscal")}
                  helperText={
                    missingCompanyFields.includes("nameFiscal")
                      ? t("company.errors.requiredField")
                      : ""
                  }
                  inputProps={{ "aria-required": true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: missingCompanyFields.includes("nameFiscal")
                          ? theme.palette.error.main
                          : theme.palette.grey[200],
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      fontFamily: "Nunito",
                      ml: 2,
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.nif")}
                  <Box
                    component="span"
                    sx={{ color: theme.palette.error.main, ml: 0.5 }}
                  >
                    *
                  </Box>
                </Typography>
                <TextField
                  id="company-field-nif"
                  fullWidth
                  value={formData.nif}
                  onChange={handleInputChange("nif")}
                  error={missingCompanyFields.includes("nif")}
                  helperText={
                    missingCompanyFields.includes("nif")
                      ? t("company.errors.requiredField")
                      : ""
                  }
                  inputProps={{ "aria-required": true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: missingCompanyFields.includes("nif")
                          ? theme.palette.error.main
                          : theme.palette.grey[200],
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      fontFamily: "Nunito",
                      ml: 2,
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.feeVariable")}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.feeVariable}
                  onChange={handleInputChange("feeVariable")}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      max: 100,
                      step: "0.01",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[200],
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.feeFixed")}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.feeFixed}
                  onChange={handleInputChange("feeFixed")}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      step: "0.01",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[200],
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.paymentTerms")}
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={formData.paymentTerms ?? ""}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      paymentTerms: event.target.value,
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[200],
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    {t("company.paymentTermsUndefined")}
                  </MenuItem>
                  <MenuItem value="30">30</MenuItem>
                  <MenuItem value="45">45</MenuItem>
                  <MenuItem value="60">60</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>

          {/* Billing Information */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: "8px",
              p: 1.5,
              mb: 0.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  color: theme.palette.grey[900],
                }}
                variant="h6"
              >
                {t("company.billing")}
              </Typography>

              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addBillingEntry}
                sx={{
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  px: 2,
                  py: 0.5,
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[900],
                  "&:hover": {
                    borderColor: theme.palette.grey[300],
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                {t("company.addBilling") || "Add Billing"}
              </Button>
            </Box>

            {billingEntries.map((billing, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  padding: 2,
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
                  borderRadius: 2,
                  outline: `1px ${theme.palette.grey[100]} solid`,
                  outlineOffset: "-1px",
                }}
              >
                {index > 0 && <Divider sx={{ my: 2 }} />}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                    variant="subtitle1"
                  >
                    {`${t("company.billing")} ${index + 1}`}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {billingEntries.length > 1 && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mr: 1 }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Nunito",
                            fontWeight: 600,
                            color: theme.palette.grey[900],
                            mr: 1,
                            fontSize: "0.9rem",
                          }}
                        >
                          {t("company.defaultBilling") || "Default Billing"}
                        </Typography>
                        <Checkbox
                          checked={billing.isDefault}
                          onChange={() => handleDefaultBillingChange(index)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    )}

                    {billingEntries.length > 1 && (
                      <Tooltip
                        title={t("company.removeBilling") || "Remove Billing"}
                      >
                        <IconButton
                          onClick={() => removeBillingEntry(index)}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <HoldedContactPicker
                    holdedContactId={billing.holdedContactId ?? ""}
                    onChangeContactId={(holdedContactId) =>
                      updateBillingEntry(index, { holdedContactId })
                    }
                    onFill={(preview) => fillBillingFromHolded(index, preview)}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("billing.fiscalName")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main, ml: 0.5 }}
                      >
                        *
                      </Box>
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.nameFiscal ?? ""}
                      onChange={(e) =>
                        updateBillingEntry(index, {
                          nameFiscal: e.target.value,
                        })
                      }
                      placeholder={t("billing.fiscalNamePlaceholder")}
                      {...billingFieldProps(index, "nameFiscal")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: isBillingFieldMissing(
                              index,
                              "nameFiscal"
                            )
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                          ml: 2,
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.nif")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main, ml: 0.5 }}
                      >
                        *
                      </Box>
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.nif ?? ""}
                      onChange={(e) =>
                        updateBillingEntry(index, { nif: e.target.value })
                      }
                      {...billingFieldProps(index, "nif")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: isBillingFieldMissing(index, "nif")
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                          ml: 2,
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.contactName")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main, ml: 0.5 }}
                      >
                        *
                      </Box>
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.contactName}
                      onChange={(e) =>
                        updateBillingEntry(index, {
                          contactName: e.target.value,
                        })
                      }
                      {...billingFieldProps(index, "contactName")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: isBillingFieldMissing(
                              index,
                              "contactName"
                            )
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                          ml: 2,
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.phoneNumber")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main, ml: 0.5 }}
                      >
                        *
                      </Box>
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.phoneNumber}
                      onChange={(e) =>
                        updateBillingEntry(index, {
                          phoneNumber: e.target.value,
                        })
                      }
                      {...billingFieldProps(index, "phoneNumber")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: isBillingFieldMissing(
                              index,
                              "phoneNumber"
                            )
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                          ml: 2,
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.email")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main, ml: 0.5 }}
                      >
                        *
                      </Box>
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      value={billing.email}
                      onChange={(e) =>
                        updateBillingEntry(index, { email: e.target.value })
                      }
                      {...billingFieldProps(index, "email")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: isBillingFieldMissing(index, "email")
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          fontFamily: "Nunito",
                          ml: 2,
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    mt: 2,
                    mb: 1,
                  }}
                  variant="subtitle1"
                >
                  {t("company.billingAddress")}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: 2,
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.address")}
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.address}
                      onChange={(e) => {
                        const newBillingEntries = [...billingEntries];
                        newBillingEntries[index] = {
                          ...newBillingEntries[index],
                          address: e.target.value,
                        };
                        setBillingEntries(newBillingEntries);
                      }}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.city")}
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.city}
                      onChange={(e) => {
                        const newBillingEntries = [...billingEntries];
                        newBillingEntries[index] = {
                          ...newBillingEntries[index],
                          city: e.target.value,
                        };
                        setBillingEntries(newBillingEntries);
                      }}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.postalCode")}
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.postalCode}
                      onChange={(e) => {
                        const newBillingEntries = [...billingEntries];
                        newBillingEntries[index] = {
                          ...newBillingEntries[index],
                          postalCode: e.target.value,
                        };
                        setBillingEntries(newBillingEntries);
                      }}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.province")}
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.province}
                      onChange={(e) => {
                        const newBillingEntries = [...billingEntries];
                        newBillingEntries[index] = {
                          ...newBillingEntries[index],
                          province: e.target.value,
                        };
                        setBillingEntries(newBillingEntries);
                      }}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("company.country")}
                    </Typography>
                    <TextField
                      fullWidth
                      value={billing.country}
                      onChange={(e) => {
                        const newBillingEntries = [...billingEntries];
                        newBillingEntries[index] = {
                          ...newBillingEntries[index],
                          country: e.target.value,
                        };
                        setBillingEntries(newBillingEntries);
                      }}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("billing.legalNote")}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={billing.legalNote ?? ""}
                    onChange={(e) => {
                      const newBillingEntries = [...billingEntries];
                      newBillingEntries[index] = {
                        ...newBillingEntries[index],
                        legalNote: e.target.value,
                      };
                      setBillingEntries(newBillingEntries);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[200],
                        },
                      },
                    }}
                  >
                    {LEGAL_NOTE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.labelKey} value={opt.value}>
                        {t(opt.labelKey)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Integrations */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: "8px",
              p: 1.5,
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Nunito",
                fontWeight: 700,
                color: theme.palette.grey[900],
                pb: 1,
              }}
              variant="h6"
              gutterBottom
            >
              {t("company.integrations")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.dv360AdvertiserId")}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.dv360AdvertiserIds}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      dv360AdvertiserIds: newValue,
                    }));
                    if (integrationErrors.dv360AdvertiserIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.dv360AdvertiserIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!integrationErrors.dv360AdvertiserIds}
                      placeholder={t("company.dv360AdvertiserId") || ""}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px", // To closely match the 50px look but accommodating tags
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.dv360AdvertiserIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.dv360AdvertiserIds && (
                  <FormHelperText error>
                    {integrationErrors.dv360AdvertiserIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.amazonAdvertiserId", {
                    defaultValue: "Amazon DSP Advertiser IDs",
                  })}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.amazonAdvertiserIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      amazonAdvertiserIds: newValue,
                    }));
                    if (integrationErrors.amazonAdvertiserIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.amazonAdvertiserIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="amazonAdvertiserIds"
                      fullWidth
                      error={!!integrationErrors.amazonAdvertiserIds}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.amazonAdvertiserIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.amazonAdvertiserIds && (
                  <FormHelperText error>
                    {integrationErrors.amazonAdvertiserIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.metaAdAccountId", {
                    defaultValue: "Meta Ad Account IDs",
                  })}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.metaAdAccountIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      metaAdAccountIds: newValue,
                    }));
                    if (integrationErrors.metaAdAccountIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.metaAdAccountIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="metaAdAccountIds"
                      fullWidth
                      error={!!integrationErrors.metaAdAccountIds}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.metaAdAccountIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.metaAdAccountIds && (
                  <FormHelperText error>
                    {integrationErrors.metaAdAccountIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.tiktokAdvertiserId", {
                    defaultValue: "TikTok Advertiser IDs",
                  })}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.tiktokAdvertiserIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      tiktokAdvertiserIds: newValue,
                    }));
                    if (integrationErrors.tiktokAdvertiserIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.tiktokAdvertiserIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="tiktokAdvertiserIds"
                      fullWidth
                      error={!!integrationErrors.tiktokAdvertiserIds}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.tiktokAdvertiserIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.tiktokAdvertiserIds && (
                  <FormHelperText error>
                    {integrationErrors.tiktokAdvertiserIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.googleAdsCustomerId", {
                    defaultValue: "Google Ads Customer IDs",
                  })}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.googleAdsCustomerIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      googleAdsCustomerIds: newValue,
                    }));
                    if (integrationErrors.googleAdsCustomerIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.googleAdsCustomerIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="googleAdsCustomerIds"
                      fullWidth
                      error={!!integrationErrors.googleAdsCustomerIds}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.googleAdsCustomerIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.googleAdsCustomerIds && (
                  <FormHelperText error>
                    {integrationErrors.googleAdsCustomerIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.redditAdAccountId", {
                    defaultValue: "Reddit Ad Account IDs",
                  })}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.redditAdAccountIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      redditAdAccountIds: newValue,
                    }));
                    if (integrationErrors.redditAdAccountIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.redditAdAccountIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="redditAdAccountIds"
                      fullWidth
                      error={!!integrationErrors.redditAdAccountIds}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.redditAdAccountIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.redditAdAccountIds && (
                  <FormHelperText error>
                    {integrationErrors.redditAdAccountIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.outbrainAccountId")}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.outbrainAccountIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      outbrainAccountIds: newValue,
                    }));
                    if (integrationErrors.outbrainAccountIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.outbrainAccountIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!integrationErrors.outbrainAccountIds}
                      placeholder={t("company.outbrainAccountId") || ""}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.outbrainAccountIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.outbrainAccountIds && (
                  <FormHelperText error>
                    {integrationErrors.outbrainAccountIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.taboolaAccountId")}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.taboolaAccountIds}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      taboolaAccountIds: newValue,
                    }));
                    if (integrationErrors.taboolaAccountIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.taboolaAccountIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!integrationErrors.taboolaAccountIds}
                      placeholder={t("company.taboolaAccountId") || ""}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.taboolaAccountIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.taboolaAccountIds && (
                  <FormHelperText error>
                    {integrationErrors.taboolaAccountIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.spotifyAdAccountId")}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.spotifyAdAccountIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      spotifyAdAccountIds: newValue,
                    }));
                    if (integrationErrors.spotifyAdAccountIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.spotifyAdAccountIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!integrationErrors.spotifyAdAccountIds}
                      placeholder={t("company.spotifyAdAccountId") || ""}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: integrationErrors.spotifyAdAccountIds
                              ? theme.palette.error.main
                              : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.spotifyAdAccountIds && (
                  <FormHelperText error>
                    {integrationErrors.spotifyAdAccountIds}
                  </FormHelperText>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("company.hivestackAdvertiserId", {
                    defaultValue: "Hivestack Advertiser IDs",
                  })}
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.hivestackAdvertiserIds || []}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      hivestackAdvertiserIds: newValue,
                    }));
                    if (integrationErrors.hivestackAdvertiserIds) {
                      setIntegrationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.hivestackAdvertiserIds;
                        return newErrors;
                      });
                    }
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="hivestackAdvertiserIds"
                      fullWidth
                      error={!!integrationErrors.hivestackAdvertiserIds}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "15px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor:
                              integrationErrors.hivestackAdvertiserIds
                                ? theme.palette.error.main
                                : theme.palette.grey[200],
                          },
                        },
                      }}
                    />
                  )}
                />
                {integrationErrors.hivestackAdvertiserIds && (
                  <FormHelperText error>
                    {integrationErrors.hivestackAdvertiserIds}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: "none",
                fontFamily: "Nunito",
                fontWeight: 700,
                px: 4,
                py: 1.5,
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : t("common.create")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NewCompany;
